import React, { useEffect, useRef, useState } from 'react';
import { Titan, Weapon, Gear, Mnemos, ArmoryLoadout, TriskellionStats, Argonaut, LoadoutStats, KratosTable, ArmoryItem, ArgonautTokens } from '../../core/types';
import '../../styles.css';
import { Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info'; // Import an info icon
import IosShareIcon from '@mui/icons-material/IosShare';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { Utils } from '../../core/utilities';
import { getArgonautStatPresentation } from '../../core/utilities/token-presentation';
import NumericField from '../../components/NumericField';
import { Constants } from '../../core/constants';
import SearchableSelect from '../../components/SearchableSelect';
import Button from '../../components/Button';

interface LoadoutSectionProps {
    titans: Titan[];
    kratosTables: KratosTable[];
    weapons: Weapon[];
    gear: Gear[];
    mnemos: Mnemos[];
    loadouts: ArmoryLoadout[];
    visibleTags: string[];
    defaultLoadout: ArmoryLoadout;
    setLoadouts: React.Dispatch<React.SetStateAction<ArmoryLoadout[]>>;
    handleCopyToLoadoutStats: (loadout: ArmoryLoadout) => void;
    advancedMode: boolean;
    onShareLoadout?: (loadout: ArmoryLoadout, index: number) => Promise<boolean>;
    onToggleFavoriteLoadout?: (loadout: ArmoryLoadout, index: number) => void;
    isFavoriteLoadout?: (loadout: ArmoryLoadout, index: number) => boolean;
}

// Recursive function to filter out properties that are null, zero, empty arrays, or empty objects
const filterObject = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map((item) => filterObject(item)).filter((item) => item !== null && item !== undefined && (Array.isArray(item) ? item.length !== 0 : Object.keys(item).length !== 0));
    } else if (typeof obj === 'object' && obj !== null) {
        const filteredObj = Object.entries(obj).reduce((acc, [key, value]) => {
            const filteredValue = filterObject(value);
            if (
                filteredValue !== null &&
                filteredValue !== 0 &&
                filteredValue !== '' &&
                !(Array.isArray(filteredValue) && filteredValue.length === 0) &&
                !(typeof filteredValue === 'object' && Object.keys(filteredValue).length === 0)
            ) {
                acc[key] = filteredValue;
            }
            return acc;
        }, {} as any);

        return Object.keys(filteredObj).length > 0 ? filteredObj : undefined;
    }

    return obj;
};

// Function to format loadout information for the tooltip (filtered with nested objects)
const formatLoadoutInfo = (loadout: ArmoryLoadout): string => {
    const filteredLoadout = filterObject(loadout);
    return 'LOADOUT DETAILS\n' + JSON.stringify(filteredLoadout, null, 2);
};

const getDefaultLoadoutTitle = (loadout: ArmoryLoadout, index: number): string => {
    if (loadout.titan?.name && loadout.weapon?.name) {
        return `${loadout.titan.name} w/ ${loadout.weapon.name}`;
    }

    return `Loadout ${index + 1}`;
};

const getSelectableItems = <T extends ArmoryItem>(items: T[], requiredItems: Array<T | undefined>, shouldShowItem: (item: T) => boolean): T[] => {
    const itemMap = new Map<string, T>();

    for (const item of items) {
        if (item?.name && shouldShowItem(item) && !itemMap.has(item.name)) {
            itemMap.set(item.name, item);
        }
    }

    for (const item of requiredItems) {
        if (item?.name && !itemMap.has(item.name)) {
            itemMap.set(item.name, item);
        }
    }

    return Array.from(itemMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const LoadoutSection: React.FC<LoadoutSectionProps> = ({
    titans,
    kratosTables,
    weapons,
    gear,
    mnemos,
    loadouts,
    visibleTags,
    defaultLoadout,
    setLoadouts,
    handleCopyToLoadoutStats,
    advancedMode,
    onShareLoadout,
    onToggleFavoriteLoadout,
    isFavoriteLoadout,
}) => {
    const [copiedLoadout, setCopiedLoadout] = useState<ArmoryLoadout | null>(null);
    const shareResetTimeoutRef = useRef<number | null>(null);
    const getTitanOptions = (currentTitan?: Titan) => getSelectableItems(titans, [defaultLoadout.titan, currentTitan], shouldShowItem);
    const getKratosTableOptions = (currentKratosTable?: KratosTable) => getSelectableItems(kratosTables, [defaultLoadout.kratosTable, currentKratosTable], shouldShowItem);
    const getWeaponOptions = (currentWeapon?: Weapon) => getSelectableItems(weapons, [defaultLoadout.weapon, currentWeapon], shouldShowItem);

    useEffect(() => {
        return () => {
            if (shareResetTimeoutRef.current !== null) {
                window.clearTimeout(shareResetTimeoutRef.current);
            }
        };
    }, []);

    const handleLoadoutChange = (index: number, field: keyof ArmoryLoadout | keyof Argonaut, value: any) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        if (['rage', 'danger', 'fate'].includes(field)) {
            value = Math.max(0, Math.min(9, value));
            updatedLoadouts[index] = {
                ...updatedLoadouts[index],
                argonaut: {
                    ...updatedLoadouts[index].argonaut,
                    [field]: value,
                },
            };
        } else if (['precisionBonus', 'powerBonus'].includes(field)) {
            updatedLoadouts[index] = {
                ...updatedLoadouts[index],
                argonaut: {
                    ...updatedLoadouts[index].argonaut,
                    [field]: value,
                },
            };
        } else if (field === 'titan') {
            const matchingKratosTable = getKratosTableOptions(updatedLoadouts[index].kratosTable).find((kt) => kt.name === (value as Titan).name);
            updatedLoadouts[index] = {
                ...updatedLoadouts[index],
                [field]: value,
                kratosTable: matchingKratosTable ?? updatedLoadouts[index].kratosTable,
            };
        } else {
            updatedLoadouts[index] = {
                ...updatedLoadouts[index],
                [field]: value,
                gear: updatedLoadouts[index].gear ?? [],
                mnemos: updatedLoadouts[index].mnemos ?? [],
            };
        }

        updatedLoadouts[index] = Utils.sanitizeArmoryLoadout(updatedLoadouts[index]);
        setLoadouts(updatedLoadouts);
    };

    const handleDuplicateLoadout = (index: number) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        const duplicatedLoadout = Utils.sanitizeArmoryLoadout(Utils.shallowCopy(updatedLoadouts[index]));
        updatedLoadouts.push(duplicatedLoadout);

        setLoadouts(updatedLoadouts);
    };

    const handleAddLoadout = () => {
        const updatedLoadouts = [...loadouts];

        const duplicatedLoadout = Utils.sanitizeArmoryLoadout(Utils.shallowCopy(defaultLoadout));
        updatedLoadouts.push(duplicatedLoadout);

        setLoadouts(updatedLoadouts);
    };

    const handleRemoveLoadout = (index: number) => {
        const updatedLoadouts = [...loadouts];
        updatedLoadouts.splice(index, 1);
        setLoadouts(updatedLoadouts);
    };

    const handleAddGear = (index: number, gearItem: Gear) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        updatedLoadouts[index].gear = updatedLoadouts[index].gear || [];

        if (!updatedLoadouts[index].gear?.find((g) => g.name === gearItem.name)) {
            updatedLoadouts[index].gear!.push(gearItem);
        }

        handleLoadoutChange(index, 'gear', updatedLoadouts[index].gear);
    };

    const handleRemoveGear = (index: number, gearName: string) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        updatedLoadouts[index].gear = updatedLoadouts[index].gear?.filter((g) => g.name !== gearName);

        handleLoadoutChange(index, 'gear', updatedLoadouts[index].gear);
    };

    const handleAddMnemos = (index: number, mnemosItem: Mnemos) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        updatedLoadouts[index].mnemos = updatedLoadouts[index].mnemos || [];

        if (!updatedLoadouts[index].mnemos?.find((m) => m.name === mnemosItem.name)) {
            updatedLoadouts[index].mnemos!.push(mnemosItem);
        }

        handleLoadoutChange(index, 'mnemos', updatedLoadouts[index].mnemos);
    };

    const handleRemoveMnemos = (index: number, mnemosName: string) => {
        const updatedLoadouts = [...loadouts];
        if (!updatedLoadouts[index]) return;

        updatedLoadouts[index].mnemos = updatedLoadouts[index].mnemos?.filter((m) => m.name !== mnemosName);

        handleLoadoutChange(index, 'mnemos', updatedLoadouts[index].mnemos);
    };

    const handleIncrement = (index: number, field: keyof TriskellionStats | keyof LoadoutStats, section: 'triskellion' | 'loadoutStats', increment: number) => {
        if (section === 'triskellion') {
            const f = field as keyof TriskellionStats;
            handleLoadoutChange(index, f, (loadouts[index].argonaut[f] ?? 0) + increment);
        }
        if (section === 'loadoutStats') {
            const f = field as keyof LoadoutStats;
            handleLoadoutChange(index, f, ((loadouts[index].argonaut[f] as number) ?? 0) + increment);
        }
    };

    const handleArgonautTokenIncrement = (index: number, field: keyof ArgonautTokens, increment: number) => {
        const current = (loadouts[index].argonaut[field] as number) ?? 0;
        const next = Math.max(0, current + increment);
        const updatedLoadouts = [...loadouts];
        updatedLoadouts[index] = {
            ...updatedLoadouts[index],
            argonaut: { ...updatedLoadouts[index].argonaut, [field]: next },
        };
        setLoadouts(updatedLoadouts);
    };

    const shouldShowItem = (item: ArmoryItem) => {
        return Utils.shouldShowTaggedEntity(item, visibleTags);
    };

    const handleShareClick = async (index: number) => {
        if (!onShareLoadout || !loadouts[index]) {
            return;
        }

        let copied = false;
        try {
            copied = await onShareLoadout(loadouts[index], index);
        } catch {
            copied = false;
        }

        if (!copied) {
            return;
        }

        if (shareResetTimeoutRef.current !== null) {
            window.clearTimeout(shareResetTimeoutRef.current);
        }

        setCopiedLoadout(loadouts[index]);
        shareResetTimeoutRef.current = window.setTimeout(() => {
            setCopiedLoadout(null);
        }, 1800);
    };

    return (
        <form className="simulation-input-section">
            {loadouts.length > 0 ? (
                <div className="loadout-section-container">
                    {loadouts.map((loadout, index) => {
                        const titanOptions = getTitanOptions(loadout.titan);
                        const kratosTableOptions = getKratosTableOptions(loadout.kratosTable);
                        const weaponOptions = getWeaponOptions(loadout.weapon);

                        return (
                            <div className="loadout-section" key={index}>
                                <div className="loadout-title-row">
                                    <input
                                        type="text"
                                        value={loadout.name}
                                        onChange={(e) => handleLoadoutChange(index, 'name', e.target.value)}
                                        onFocus={(e) => e.currentTarget.select()}
                                        placeholder={getDefaultLoadoutTitle(loadout, index)}
                                        className="loadout-title-input"
                                        aria-label={`Loadout ${index + 1} name`}
                                    />
                                    <Tooltip title={<pre className="tooltip-pre">{formatLoadoutInfo(loadout)}</pre>} arrow enterDelay={500} enterNextDelay={500}>
                                        <Typography component="span" className="loadout-title-info">
                                            <InfoIcon className="info-icon" />
                                        </Typography>
                                    </Tooltip>
                                </div>

                                <div className="loadout-select-row">
                                    <label className="loadout-select-label">Titan:</label>
                                    <div className="loadout-input-group">
                                        <SearchableSelect
                                            value={loadout.titan?.name ?? ''}
                                            options={titanOptions.map((titan) => ({ value: titan.name }))}
                                            onChange={(value) => handleLoadoutChange(index, 'titan', titanOptions.find((t) => t.name === value)!)}
                                            ariaLabel={`Loadout ${index + 1} titan`}
                                        />
                                    </div>
                                </div>

                                <div className="loadout-select-row">
                                    <label className="loadout-select-label">Kratos Table:</label>
                                    <div className="loadout-input-group">
                                        <SearchableSelect
                                            value={loadout.kratosTable?.name}
                                            options={kratosTableOptions.map((kratosTable) => ({ value: kratosTable.name }))}
                                            onChange={(value) => handleLoadoutChange(index, 'kratosTable', kratosTableOptions.find((kt) => kt.name === value)!)}
                                            ariaLabel={`Loadout ${index + 1} kratos table`}
                                        />
                                    </div>
                                </div>

                                <div className="loadout-select-row">
                                    <label className="loadout-select-label">Weapon:</label>
                                    <div className="loadout-input-group">
                                        <SearchableSelect
                                            value={loadout.weapon?.name ?? ''}
                                            options={weaponOptions.map((weapon) => ({ value: weapon.name }))}
                                            onChange={(value) => handleLoadoutChange(index, 'weapon', weaponOptions.find((w) => w.name === value)!)}
                                            ariaLabel={`Loadout ${index + 1} weapon`}
                                        />
                                    </div>
                                </div>

                                {advancedMode && (
                                    <>
                                        <div className="loadout-select-row loadout-select-row-collection">
                                            <label className="loadout-select-label">Other Gear:</label>
                                            <div className="loadout-input-group">
                                                <SearchableSelect
                                                    value=""
                                                    options={gear
                                                        .filter((gear) => shouldShowItem(gear))
                                                        .map((gear) => ({ value: gear.name }))}
                                                    onChange={(value) => handleAddGear(index, gear.find((g) => g.name === value)!)}
                                                    placeholder="(Select Other Gear)"
                                                    ariaLabel={`Loadout ${index + 1} other gear`}
                                                    clearOnSelect
                                                />
                                                <ul className="selected-items">
                                                    {loadout.gear
                                                        ?.sort((a, b) => a.name.localeCompare(b.name))
                                                        .map((g) => (
                                                            <li key={g.name}>
                                                                <Button type="button" className="remove-item" variant="ghost" size="sm" aria-label={`Remove ${g.name}`} onClick={() => handleRemoveGear(index, g.name)}>
                                                                    x
                                                                </Button>
                                                                {g.name}
                                                            </li>
                                                        ))}
                                                </ul>
                                            </div>
                                        </div>

                                        <div className="loadout-select-row loadout-select-row-collection">
                                            <label className="loadout-select-label">Mnemos:</label>
                                            <div className="loadout-input-group">
                                                <SearchableSelect
                                                    value=""
                                                    options={mnemos
                                                        .filter((mnemos) => shouldShowItem(mnemos))
                                                        .map((mnemos) => ({ value: mnemos.name }))}
                                                    onChange={(value) => handleAddMnemos(index, mnemos.find((m) => m.name === value)!)}
                                                    placeholder="(Select Mnemos)"
                                                    ariaLabel={`Loadout ${index + 1} mnemos`}
                                                    clearOnSelect
                                                />
                                                <ul className="selected-items">
                                                    {loadout.mnemos
                                                        ?.sort((a, b) => a.name.localeCompare(b.name))
                                                        .map((m) => (
                                                            <li key={m.name}>
                                                                <Button type="button" className="remove-item" variant="ghost" size="sm" aria-label={`Remove ${m.name}`} onClick={() => handleRemoveMnemos(index, m.name)}>
                                                                    x
                                                                </Button>
                                                                {m.name}
                                                            </li>
                                                        ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}

                        <div className="loadout-row">
                            <NumericField
                                label="Rage"
                                value={loadout.argonaut.rage || 0}
                                onDecrement={() => handleIncrement(index, 'rage', 'triskellion', -1)}
                                onIncrement={() => handleIncrement(index, 'rage', 'triskellion', 1)}
                                accent={getArgonautStatPresentation('rage')}
                            />
                            <NumericField
                                label="Fate"
                                value={loadout.argonaut.fate || 0}
                                onDecrement={() => handleIncrement(index, 'fate', 'triskellion', -1)}
                                onIncrement={() => handleIncrement(index, 'fate', 'triskellion', 1)}
                                accent={getArgonautStatPresentation('fate')}
                            />
                            <NumericField
                                label="Danger"
                                value={loadout.argonaut.danger || 0}
                                onDecrement={() => handleIncrement(index, 'danger', 'triskellion', -1)}
                                onIncrement={() => handleIncrement(index, 'danger', 'triskellion', 1)}
                                accent={getArgonautStatPresentation('danger')}
                            />
                        </div>

                        {advancedMode && (
                            <>
                                <div className="loadout-row">
                                    <NumericField
                                        label="Precision Bonus"
                                        value={loadout.argonaut.precisionBonus ?? 0}
                                        onDecrement={() => handleIncrement(index, 'precisionBonus', 'loadoutStats', -1)}
                                        onIncrement={() => handleIncrement(index, 'precisionBonus', 'loadoutStats', 1)}
                                    />
                                    <NumericField
                                        label="Power Bonus"
                                        value={loadout.argonaut.powerBonus ?? 0}
                                        onDecrement={() => handleIncrement(index, 'powerBonus', 'loadoutStats', -1)}
                                        onIncrement={() => handleIncrement(index, 'powerBonus', 'loadoutStats', 1)}
                                    />
                                </div>
                            </>
                        )}

                        {/* Argonaut token inputs — balanced rows (max 3 per row, no lone items), advanced mode only */}
                        {advancedMode &&
                            (() => {
                                type TokenDef = { label: string; key: keyof ArgonautTokens };
                                const tokens: TokenDef[] = [
                                    { label: 'Ambrosia', key: 'ambrosia' },
                                    { label: 'Despair', key: 'despair' },
                                    ...(visibleTags.includes(Constants.SpoilerTag.CYCLE_III) ? [{ label: 'Bleed', key: 'bleed' as keyof ArgonautTokens }] : []),
                                    ...(visibleTags.includes(Constants.SpoilerTag.CYCLE_IV) ? [{ label: 'Midas', key: 'midas' as keyof ArgonautTokens }] : []),
                                    ...(visibleTags.includes(Constants.SpoilerTag.CYCLE_V) ? [{ label: 'Pain', key: 'pain' as keyof ArgonautTokens }] : []),
                                ];
                                // Split into balanced rows: max 3 per row, avoid lone items
                                // 1→[1], 2→[2], 3→[3], 4→[2,2], 5→[3,2]
                                const n = tokens.length;
                                const rowSize = n <= 3 ? n : n === 4 ? 2 : 3;
                                const rows: TokenDef[][] = [];
                                for (let i = 0; i < n; i += rowSize) rows.push(tokens.slice(i, i + rowSize));
                                return rows.map((row, i) => (
                                    <div key={i} className="loadout-row">
                                        {row.map(({ label, key }) => (
                                            <NumericField
                                                key={key}
                                                label={label}
                                                value={loadout.argonaut[key] || 0}
                                                onDecrement={() => handleArgonautTokenIncrement(index, key, -1)}
                                                onIncrement={() => handleArgonautTokenIncrement(index, key, 1)}
                                            />
                                        ))}
                                    </div>
                                ));
                            })()}

                        <div className="spacer" />
                        <div className="input-section-button-container">
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => onToggleFavoriteLoadout?.(loadouts[index], index)}>
                                {isFavoriteLoadout?.(loadouts[index], index) ? <StarIcon className="loadout-action-icon" /> : <StarBorderIcon className="loadout-action-icon" />}
                                {isFavoriteLoadout?.(loadouts[index], index) ? 'Unfavorite' : 'Favorite'}
                            </Button>
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => handleShareClick(index)}>
                                <IosShareIcon className="loadout-action-icon" />
                                {copiedLoadout === loadouts[index] ? 'Copied Link' : 'Share'}
                            </Button>
                            <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => handleDuplicateLoadout(index)}>
                                Duplicate
                            </Button>
                            {advancedMode && (
                                <Button className="compact-loadout-action-button" variant="secondary" fullWidth onClick={() => handleCopyToLoadoutStats(loadouts[index])}>
                                    Duplicate to Manual Loadout
                                </Button>
                            )}
                            <Button className={advancedMode ? 'loadout-action-button-wide' : 'compact-loadout-action-button'} variant="danger" fullWidth onClick={() => handleRemoveLoadout(index)}>
                                Remove
                            </Button>
                        </div>
                    </div>
                        );
                    })}
                </div>
            ) : (
                <div className="loadout-empty-state">
                    <p className="loadout-empty-message">No armory loadouts yet.</p>
                </div>
            )}

            <div className={`add-loadout-container${loadouts.length === 0 ? ' add-loadout-container-empty' : ''}`}>
                <Button className="add-loadout-button" variant="secondary" size="lg" onClick={handleAddLoadout}>
                    Add Loadout
                </Button>
            </div>
        </form>
    );
};

export default LoadoutSection;
